import { FileIcon } from 'lucide-react'
import { useEffect, useRef } from 'react'

import type { QuotingCsvFileContents } from 'utils/web_api.schemas'

export interface CsvSheetProps {
  data: QuotingCsvFileContents
  onRowHover?: (index: number | null) => void
  highlightedRowIndices?: Set<number>
  scrollToHighlighted?: boolean
}

export default function InputCensusCsvSheet ({ data, onRowHover, highlightedRowIndices, scrollToHighlighted }: CsvSheetProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map())

  useEffect(() => {
    if (scrollToHighlighted && highlightedRowIndices && highlightedRowIndices.size > 0) {
      // Scroll to the first highlighted row within this container only
      const firstHighlightedIndex = Math.min(...highlightedRowIndices)
      const row = rowRefs.current.get(firstHighlightedIndex)
      const container = containerRef.current
      if (row && container) {
        // Center the row within the container, not using scrollIntoView to avoid parent container scrolling
        const scrollTop = row.offsetTop - container.clientHeight / 2 + row.clientHeight / 2
        container.scrollTo({ top: scrollTop, behavior: 'smooth' })
      }
    }
  }, [highlightedRowIndices, scrollToHighlighted])

  return (
    <div ref={containerRef} className='border border-gray-200 rounded-md bg-white overflow-x-auto max-h-[70vh] overflow-y-auto'>
      <div className='white border border-gray-200 rounded-t-md p-2 flex items-center gap-2'>
        <FileIcon className='h-4 w-4' />
        <h3 className='text-sm font-medium text-gray-600 italic underline decoration-dotted'>
          Input Census CSV File
        </h3>
      </div>
      <table>
        <thead className='sticky top-0'>
          <tr className='bg-gray-100'>
            <th className='border border-gray-200 p-0.5' />
            {data.headers.map((header, idx) => (
              <th key={idx} className='border border-gray-200 py-1 px-2 text-center font-bold whitespace-nowrap'>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, rowIdx) => (
            <tr
              key={rowIdx}
              ref={(el) => {
                if (el) {
                  rowRefs.current.set(rowIdx, el)
                } else {
                  rowRefs.current.delete(rowIdx)
                }
              }}
              onMouseEnter={() => onRowHover?.(rowIdx)}
              onMouseLeave={() => onRowHover?.(null)}
              className={`cursor-default ${highlightedRowIndices?.has(rowIdx) ? 'bg-yellow-200' : ''}`}
            >
              <td className={`text-center italic border border-gray-200 py-1 px-2 whitespace-nowrap ${highlightedRowIndices?.has(rowIdx) ? 'bg-yellow-200' : 'bg-gray-100'}`}>
                {rowIdx + 1}
              </td>
              {Object.values(row).map((value, valueIdx) => (
                <td key={valueIdx} className='border border-gray-100 py-1 px-2 whitespace-nowrap'>{String(value)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
